import React, { useEffect, useState } from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import {
  DollarCircleFilled, EuroCircleFilled, PoundCircleFilled, PauseCircleOutlined,
  PlayCircleOutlined, CheckCircleFilled, CheckCircleTwoTone,
  ExclamationCircleTwoTone,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Alert, Avatar, Card, Collapse, List, Button, Input, Table, Tooltip, Tag, Space, Typography } from 'antd';
import moment from 'moment';

import { useClientChangeHistory } from '../../models/useClientChangeHistory';
import { capitalize } from '../../utils/ProductSwitcher';
import {ccyIsoCodeToSymbolMap, countryFlags } from '../../utils/CurrencyUtils';
import StrikeClientModel from '../../models/StrikeClientModel';
import { getAppBaseUrl } from '../../utils/URLBuilder';

import { AdminGridClientConnectAccounts } from './AdminGridClientConnectAccounts';

const isLocalHost = window.location.hostname === 'localhost';
const URLPath = getAppBaseUrl();
const { Search } = Input;
const { Panel } = Collapse;
const { Text } = Typography;

const ccy = ccyIsoCodeToSymbolMap;

const locale = {
  'IE': 'Ireland',
  'GB': 'English',
  'US': 'US',
};

const ccy_icons = {
  'EUR': <EuroCircleFilled style={{ fontSize: '30px', color: '#00c' }} />,
  'USD': <DollarCircleFilled style={{ fontSize: '30px', color: '#08c' }} />,
  'GBP': <PoundCircleFilled style={{ fontSize: '30px', color: '#70c' }} />,
};

function iconForCCY(ccy) {
  let icon = ccy_icons[ccy];
  return (icon) ? icon : <DollarCircleFilled style={{ fontSize: '30px', color: '#08c' }} />;
}

function formatLoginMethod(authProvider) {
  if (!authProvider) return 'Unknown';
  const normalized = `${authProvider}`.toLowerCase();
  if (normalized === 'google') return 'Google';
  if (normalized === 'apple') return 'Apple';
  if (normalized === 'email') return 'Email';
  if (normalized === 'phone') return 'Phone';
  if (normalized === 'anonymous') return 'Anonymous';
  return capitalize(normalized);
}

export const AdminClientsListItemMetaDescription = ({ client, locStrings, isAdmin }) => {
  const localeText = locale[client.locale] || client.locale;
  const clientLocale = localeText ? (<Tag>{`${(localeText)}`}</Tag>) : null;
  const phoneNumber = client.phoneNumber || "None";
  const employerName = client.employerName ? (<p>Employer Name: {client.employerName}</p>) : null;
  const profileBio = client.profileBio ? (<p>{client.profileBio}</p>) : null;
  const clientId = client.id;
  const isConnect = client.country === 'US';
  const statusIcon = client.status === 'live' ? <CheckCircleFilled color='darkgreen' /> : <PauseCircleOutlined color='grey' />;
  
  const statusTag = client.status ? (
    <Tag
      color={client.status === 'live' ? 'darkgreen' : 'orange'}
      icon={statusIcon}
    >
      {capitalize(client.status)}
    </Tag>
  ) : null;

  // Why is this client paused? The onDeleteAuthUser sweep stamps
  // pausedReason:'auth_user_deleted' on orphans it reclaims (2026-07-29 hotfix) —
  // surfacing it here lets admins tell "swept orphan, ignore/clean up" from
  // "manually paused, may need attention" at a glance.
  const pausedReasonTag = client.status === 'paused' && client.pausedReason ? (
    <Tag color={client.pausedReason === 'auth_user_deleted' ? 'red' : 'volcano'}>
      {client.pausedReason === 'auth_user_deleted' ? 'Swept: auth user deleted' : capitalize(client.pausedReason)}
    </Tag>
  ) : null;

  // Server-observed IP recorded at terms acceptance (write-once, all clients).
  // Fraud correlation at a glance: the same IP across several clients links
  // identities that different emails/providers hide. The field CAN be empty —
  // the recording call is fire-and-forget — so undefined/''/whitespace all
  // simply render nothing.
  const termsIP = `${client.agreedTermsIP || ''}`.trim();
  const termsIPTag = termsIP ? (
    <Tag color="purple"><code>{termsIP}</code></Tag>
  ) : null;

  return (
          <div>
              <Space direction='vertical'>
                {profileBio}
                <Space direction='horizontal'>
                  {statusTag}
                  {pausedReasonTag}
                  <Tag color="geekblue">Login: {formatLoginMethod(client.authProvider)}</Tag>
                  {termsIPTag}
                  {phoneNumber}
                  {employerName}
                  {clientLocale}
                </Space>
                { clientId && (
                  <AdminGridClientConnectAccounts clientId={clientId} isConnect={isConnect} />
                )}
              </Space>
          </div>
      );

};

export const AdminClientsListItemMetaTitle = ({ client, locStrings }) => {
  const clientPath = `${URLPath}/admin/clients/${client.id}`;
  const clientLink = (<a href={clientPath}>{client.displayName}</a>);
  const clientEmailVerifiedColor = !!client.emailVerified ? "#52c41a" : "#ff0000";
  const checkIcon = <CheckCircleTwoTone twoToneColor={clientEmailVerifiedColor} />;
  const exclaimIcon = <ExclamationCircleTwoTone twoToneColor={clientEmailVerifiedColor} />;
  const emailVerfiedIcon = !!client.emailVerified ? checkIcon : exclaimIcon;
  const clientCountry = client.country ? countryFlags[client.country] : null;

  return (
          <div>
              <p>
                <span>
                  {`${clientCountry || ''}`}
                  { clientLink }
                  {` `}
                  { emailVerfiedIcon }
                </span>
                {`  ${client.email || ''} `}
              </p>
          </div>
      );

};


export const AdminClientsListItem = ({ client, locStrings, content: Content }) => {

  const clientCreatedAt = client.createdAt; 
  const clientUpdatedAt = client.updatedAt || client.createdAt;
  const clientType = client.type || "unknown";
  const clientTypeColor = clientType === "individual" ? "green" : "blue";
  

  let actions = [];

  if(clientCreatedAt) {
    actions = [ 
      `Updated ${moment(clientUpdatedAt.toMillis()).format("MMM DD, YYYY").toString()}`,
      `Since ${moment(clientCreatedAt.toMillis()).format("MMM DD, YYYY").toString()}`,
    ];
  }

  actions = [ 
    ...actions,
    <Tag key="action-client-type" color={clientTypeColor}>{clientType}</Tag>,
  ];

  const handleTeamsAccessChange = async () =>{
      const clientModel = new StrikeClientModel({ clientId: client.id });
      const isTeamsAllowed = buttonText === 'Allow Teams'
      await clientModel.setTeamsAllowedSetting({ isTeamsAllowed });
  }

  let buttonText = 'Remove Teams';
  let icon = <PauseCircleOutlined/>;
  let tooltipText = 'This client will lose Teams access';

  if (client.hasOwnProperty('teamsAllowed')) {
    if (!client.teamsAllowed) {
      buttonText = 'Allow Teams';
      icon = <PlayCircleOutlined/>;
      tooltipText = 'This client will get Teams access';
    }
  }

    return (
        <List.Item
          key={client.id}
          actions={[...actions, <Tooltip title={tooltipText}><Button key="1" icon={icon} onClick={() => handleTeamsAccessChange()} type="secondary" danger={buttonText === 'Remove Teams'}>
              {buttonText}
          </Button> </Tooltip>]}
        >
          <List.Item.Meta
            avatar={<Avatar src={client.avatar} size='large'/>}
            title={<AdminClientsListItemMetaTitle client={client} locStrings={locStrings}/>}
            description={<AdminClientsListItemMetaDescription client={client} locStrings={locStrings}/>}
          />
          { Content && (<Content client={client} locStrings={locStrings} />)}
        </List.Item>
      );

};


export const AdminTeamClientListItem = ({ client, locStrings }) => {
  const clientStatus = `${capitalize(client.status || "live")}`;
  const clientStatusColor = "success";
  const clientCreatedAt = client.createdAt;

  let actions = null;

  if(clientCreatedAt) {
    actions = <Text>{`Since ${moment(clientCreatedAt.toMillis()).format("MMM DD, YYYY").toString()}`}</Text>;
  }

  actions = <Space direction='horizontal'>
    {actions}
  </Space>

  return (
      <List.Item
        key={client.id}
        actions={[<Tag color={clientStatusColor}>{client.profileBio}</Tag>]}
      >
        <List.Item.Meta
          avatar={<Avatar src={client.avatar} size='large'/>}
          title={<AdminClientsListItemMetaTitle client={client} locStrings={locStrings}/>}
          description={actions}
        />
      </List.Item>
    );
};

/**
 * Admin-only card showing sign-up sourcing and marketing opt-in.
 * Used on client home page for tracking.
 */
export const AdminSourcingBox = ({ client }) => {
  if (!client) return null;
  const signupReason = client.signupOriginReason || client.originReason || '—';
  const path = client.signupOriginPath || client.originPath;
  const href = path && path.startsWith('http') ? path : path ? `${window.location.origin}${path.startsWith('/') ? path : '/' + path}` : null;
  const marketingOptIn = client.marketingOptIn;
  const email = client.email || client.authEmail || '—';
  const params = client.signupOriginParams || client.originParams || {};
  const reasonLower = `${signupReason}`.toLowerCase();
  const isJoinTeam = reasonLower === 'join_team';
  const isClaimTag = reasonLower === 'claim_tag';
  const teamOwnerClientId = params.teamOwnerClientId || params.teamOwnerClient;
  const teamId = params.teamId;
  const tagId = params.tagId || params.tagUid;
  const adminTeamHref = (teamOwnerClientId && teamId) ? `${URLPath}/admin/clients/${teamOwnerClientId}/teams/${teamId}` : null;
  const adminTagHref = (teamOwnerClientId && teamId && tagId)
    ? `${URLPath}/admin/clients/${teamOwnerClientId}/teams/${teamId}/tags/${tagId}`
    : (params.clientId && tagId)
      ? `${URLPath}/admin/clients/${params.clientId}/tags/${tagId}`
      : null;

  return (
    <Collapse
      defaultActiveKey={[]}
      style={{ marginBottom: 16, borderColor: '#1890ff' }}
    >
      <Panel
        header={
          <Space>
            <InfoCircleOutlined />
            <Text strong>Admin only — Sourcing & marketing</Text>
          </Space>
        }
        key="sourcing"
      >
        <Alert
          message="Used to track sign-up sourcing. Not visible to clients."
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
        />
        <Space direction="vertical" size={4}>
          <div>
            <Text type="secondary">Sign-up origin reason: </Text>
            <Text>{signupReason}</Text>
            {isJoinTeam && adminTeamHref && (
              <>
                {' '}
                <a href={adminTeamHref} target="_blank" rel="noopener noreferrer">View Team</a>
              </>
            )}
            {isClaimTag && adminTagHref && (
              <>
                {' '}
                <a href={adminTagHref} target="_blank" rel="noopener noreferrer">View Tag</a>
              </>
            )}
          </div>
          <div>
            <Text type="secondary">Sign-up link: </Text>
            {path ? (
              <a href={href} target="_blank" rel="noopener noreferrer">{path}</a>
            ) : (
              <Text>—</Text>
            )}
          </div>
          <div>
            <Text type="secondary">Marketing opt in: </Text>
            <Text type={marketingOptIn ? 'success' : 'secondary'}>
              {marketingOptIn ? 'Yes' : 'No'}
            </Text>
            {marketingOptIn && (
              <Text type="secondary"> — {email}</Text>
            )}
          </div>
          <div>
            <Text type="secondary">Agreed terms at: </Text>
            <Text style={{ fontSize: 12 }}>
              {client.agreedTermsAt
                ? moment(client.agreedTermsAt?.toMillis?.() ?? client.agreedTermsAt).format('LLL')
                : '—'}
            </Text>
          </div>
        </Space>
      </Panel>
    </Collapse>
  );
};

/** Format before/after state into a compact summary string */
function formatStateChange(prev, next) {
  if (!prev && !next) return null;
  const prevObj = prev && typeof prev === 'object' ? prev : {};
  const nextObj = next && typeof next === 'object' ? next : {};
  const keys = new Set([...Object.keys(prevObj), ...Object.keys(nextObj)]);
  const parts = [];
  keys.forEach((k) => {
    const vPrev = prevObj[k];
    const vNext = nextObj[k];
    if (vPrev !== vNext) {
      const sPrev = vPrev == null ? 'null' : String(vPrev);
      const sNext = vNext == null ? 'null' : String(vNext);
      parts.push(`${k}: ${sPrev} → ${sNext}`);
    }
  });
  return parts.length ? parts.join('; ') : null;
}

/**
 * Admin-only collapsible box showing client change history.
 * Subscribes to clients/{clientId}/changehistory subcollection (real-time).
 * Collapsed by default. Displays dates, reasons, and before/after state in reverse chronological order.
 */
export const AdminChangeHistoryBox = ({ clientId }) => {
  const changeHistory = useClientChangeHistory(clientId);

  if (!clientId || !changeHistory || changeHistory.length === 0) {
    return null;
  }

  const sorted = [...changeHistory].sort((a, b) => {
    const dateA = a.createdAt || a.date;
    const dateB = b.createdAt || b.date;
    const msA = dateA?.toMillis?.() ?? dateA;
    const msB = dateB?.toMillis?.() ?? dateB;
    return (msB || 0) - (msA || 0);
  });

  return (
    <Collapse
      defaultActiveKey={[]}
      style={{ marginBottom: 16, borderColor: '#1890ff' }}
    >
      <Panel header={<Text strong>Change history</Text>} key="changeHistory">
        <List
          size="small"
          dataSource={sorted}
          renderItem={(item, idx) => {
            const date = item.createdAt || item.date;
            const dateStr = date?.toMillis
              ? moment(date.toMillis()).format('LLL')
              : date
                ? moment(date).format('LLL')
                : '—';
            const reason = item.reason || item.remarks || '—';
            const stateChange = formatStateChange(item.previousState, item.nextState);
            const eventType = item.eventType ? ` (${item.eventType})` : '';
            return (
              <List.Item key={item.id || idx}>
                <div>
                  <Text type="secondary">{dateStr}{eventType}</Text>
                  <br />
                  <Text>{reason}</Text>
                  {stateChange && (
                    <>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>{stateChange}</Text>
                    </>
                  )}
                </div>
              </List.Item>
            );
          }}
        />
      </Panel>
    </Collapse>
  );
};

export const IDVTable = ({ client }) => {
  let clientIdvStatusChanges = [];
  client && client.recentStatusHistory && client.recentStatusHistory.map((doc) => {
    const updatedByDisplayName = doc.updatedBy && doc.updatedBy.displayName ? doc.updatedBy.displayName : null;
    const updatedByEmail = doc.updatedBy && doc.updatedBy.email ? doc.updatedBy.email : null;
    const updatedByFallback = doc.updatedBy && typeof doc.updatedBy === 'string' ? doc.updatedBy : '-';
    const updatedBy = updatedByDisplayName || updatedByEmail || updatedByFallback;
    
    const historyEntry = {
      createdAt: doc.createdAt,
      statusChange: `${doc.previousStatus} → ${doc.newStatus}`,
      updatedBy,
      remarks: doc.remarks,
    }
    clientIdvStatusChanges.push(historyEntry);
  })

  const columns = [
    {
      title: 'Status Change',
      dataIndex: 'statusChange',
      key: 'statusChange',
      align: 'center',
      render:
        (statusChange) => (
          <p>
            {statusChange}
          </p>
        )
    },
    {
      title: 'Updated At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      render:
        (createdAt) => (
          <p key={createdAt}>
            {moment(createdAt.toMillis()).format('LLL')}
          </p>
        )
    },
    {
      title: 'Updated By',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      align: 'center',
      render:
        (updatedBy) => (
          <p key={updatedBy}>
            {updatedBy && updatedBy.displayName ? updatedBy.displayName : updatedBy}
          </p>
        )
    },
    {
      title: 'Reason',
      dataIndex: 'remarks',
      key: 'remarks',
      align: 'center',
      render:
        (remarks) => (
          <p key={remarks} style={{ whiteSpace: 'pre-wrap', textAlign: 'left', margin: 0 }}>
            {remarks ? remarks : ''}
          </p>
        )
    },
  ];

  return (
    <Collapse defaultActiveKey={['0']}>
      <Panel header="idVerificationStatus changes" key="1">
        {!clientIdvStatusChanges.length ?
          (<div>
            <p> <i>No idVerificationStatus change history</i> </p>
            {client.idVerificationStatus ?
              <p>Current Status: <b>{client.idVerificationStatus}</b> </p> :
              <p>Current Status: <b>No Status</b> </p>
            }
          </div>)
          :
          <>
            <p>Current Status: <b>{client.idVerificationStatus}</b> </p>
            <Table
              align='center'
              columns={columns}
              dataSource={clientIdvStatusChanges}
              size="small"
              pagination={{ hideOnSinglePage: true}}
              rowKey="Id"
            />
          </>
        }
      </Panel>
    </Collapse>
  );
}

const AdminClientsListItemDetail = ({ client, locStrings }) => {
  return (
          <></>
      );

};

export const ClientsSearch = ({ initialValue, callbackOnSearch, disabled, loading, locStrings }) => {

  const onSearch = (value) => {
    return callbackOnSearch ? callbackOnSearch(value) : null;
  };

  return (
    <Search 
      placeholder="input search text" 
      enterButton="Search" 
      size="large" 
      loading={!!loading}
      allowClear
      onSearch={onSearch}
      disabled={disabled}
    />
  );

};



export const AdminClientsList = ({ condition, locStrings, clients, ...rest }) => {

  return (
    <List
      itemLayout="vertical"
      size='large'
      pagination={true}
      style={{ textAlign: 'start', margin: '2px', padding: '2px' }}
      loading={!condition()}
      dataSource={clients}
      renderItem={client => (
        <AdminClientsListItem 
          client={client} 
          locStrings={locStrings} 
          content={AdminClientsListItemDetail} 
        />
      )}
    />
  );

};
